/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: MenuItemSelect.tsx
 🗓️ Created: 2025-01-10
 🧩 Description:
    Custom menu item selection component with search functionality,
    for selecting startup modules and navigation destinations.

 🔧 Notes:
    - Implements searchable dropdown with search functionality
    - Supports keyboard navigation (arrow keys, enter, escape)
    - Dynamically loads menu items from API
    - Optimized for performance with filtered results
    - Integrates with form validation systems

 🔁 Last Updated: 2025-01-10 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo } from 'react';
import { ChevronDown, Search, Navigation } from 'lucide-react';

interface MenuItemOption {
  id: number;
  name: string;
  path: string;
}

interface MenuItemSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: MenuItemOption[];
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
}

export const MenuItemSelect: React.FC<MenuItemSelectProps> = ({
  value,
  onChange,
  options,
  className = '',
  placeholder = 'Select module...',
  disabled = false,
  loading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return options;
    }
    
    const query = searchQuery.toLowerCase();
    return options.filter(option => 
      option.name.toLowerCase().includes(query) ||
      option.path.toLowerCase().includes(query)
    );
  }, [searchQuery, options]);

  // Get selected option label
  const selectedLabel = useMemo(() => {
    const selected = options.find(option => option.path === value);
    return selected ? selected.name : placeholder;
  }, [value, placeholder, options]);

  // Handle selection
  const handleSelect = (path: string) => {
    onChange(path);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Handle keyboard
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled || loading}
        className={`
          w-full px-3 py-2 text-sm border rounded bg-background text-foreground 
          focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent
          flex items-center justify-between gap-2
          ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}
          ${isOpen ? 'ring-1 ring-primary border-transparent' : 'border-border'}
          ${className}
        `}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Navigation className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="truncate text-left">
            {loading ? 'Loading menu items...' : selectedLabel}
          </span>
        </div>
        <ChevronDown 
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search modules..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                No modules found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleSelect(option.path)}
                  className={`
                    w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                    flex items-center gap-2
                    ${value === option.path ? 'bg-primary/10 text-primary' : 'text-foreground'}
                  `}
                >
                  <Navigation className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate font-medium">{option.name}</span>
                    <span className="text-xs text-muted-foreground truncate">{option.path}</span>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer with count */}
          {filteredOptions.length > 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border bg-muted/30">
              {filteredOptions.length} module{filteredOptions.length !== 1 ? 's' : ''} found
            </div>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setIsOpen(false);
            setSearchQuery('');
          }}
        />
      )}
    </div>
  );
};

export default MenuItemSelect;

